import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  Divider,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import toast from "react-hot-toast";
import axios from "axios";
import { URL } from "../config";

const AddPackageModal = ({ onClose, packageData, refresh }) => {
  const [loading, setLoading] = useState(false);
  const [packageTypes, setPackageTypes] = useState([]);
  const [packageSubTypes, setPackageSubTypes] = useState([]);

  const [formData, setFormData] = useState({
    fkpackageTypeId: "",
    fkpackageSubTypeId: "",
    packageTitle: "",
    packageDetails: "",
    packagePrice: "",
  });

  useEffect(() => {
    fetchPackageTypes();
    fetchPackageSubTypes();
  }, []);

  useEffect(() => {
    if (!packageData) return;
    setFormData({
      fkpackageTypeId: packageData.fkpackageTypeId || "",
      fkpackageSubTypeId: packageData.fkpackageSubTypeId || "",
      packageTitle: packageData.packageTitle || "",
      packageDetails: packageData.packageDetails || "",
      packagePrice: packageData.packagePrice || "",
    });
  }, [packageData]);

  const fetchPackageTypes = async () => {
    try {
      const res = await axios.get(`${URL}/packagetypes`);
      setPackageTypes(res.data || []);
    } catch {
      toast.error("Failed to load package types");
    }
  };

  const fetchPackageSubTypes = async () => {
    try {
      const res = await axios.get(`${URL}/packagesubtypes`);
      setPackageSubTypes(res.data || []);
    } catch {
      toast.error("Failed to load package subtypes");
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async () => {
    const {
      fkpackageTypeId,
      fkpackageSubTypeId,
      packageTitle,
      packagePrice,
    } = formData;

    if (!fkpackageTypeId || !fkpackageSubTypeId || !packageTitle || !packagePrice) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("fkpackageTypeId", fkpackageTypeId);
      fd.append("fkpackageSubTypeId", fkpackageSubTypeId);
      fd.append("packageTitle", packageTitle);
      fd.append("packageDetails", formData.packageDetails);
      fd.append("packagePrice", packagePrice);

      if (packageData) {
        await axios.patch(`${URL}/packages/${packageData.packageId}`, fd);
        toast.success("Package updated successfully");
      } else {
        await axios.post(`${URL}/packages`, fd);
        toast.success("Package added successfully");
      }

      refresh?.();
      onClose?.();
    } catch {
      toast.error("Failed to save package");
    } finally {
      setLoading(false);
    }
  };


  const fieldStyle = {
    "& .MuiOutlinedInput-root": {
      minHeight: "28px",
      fontSize: "14px",
      "& fieldset": { borderColor: "#c4c4c4" },
      "&:hover fieldset": { borderColor: "#184459ff" },
      "&.Mui-focused fieldset": {
        borderColor: "#c4c4c4",
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "18px",
      color: "#184459ff",
      fontWeight: 600,
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#184459ff" },
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        p: 2,
      }}
    >
      <Box
        sx={{
          width: { xs: "90%", sm: 450 },
          bgcolor: "#fff",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
      
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" textAlign="center" sx={{ color: "#184459ff" }}>
            {packageData ? "Edit Package" : "Add Package"}
          </Typography>
          <Divider sx={{ mt: 1 }} />
        </Box>

       
        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
          <Stack spacing={2}>
            <TextField
              select
              label="Package Type"
              size="small"
              fullWidth
              value={formData.fkpackageTypeId}
              onChange={(e) =>
                handleChange("fkpackageTypeId", e.target.value)
              }
              sx={fieldStyle}
              InputLabelProps={{ shrink: true }}
            >
              {packageTypes.map((p) => (
                <MenuItem key={p.packageTypeId} value={p.packageTypeId}>
                  {p.packageTypeName}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Package Subtype"
              size="small"
              fullWidth
              value={formData.fkpackageSubTypeId}
              onChange={(e) =>
                handleChange("fkpackageSubTypeId", e.target.value)
              }
              sx={fieldStyle}
              InputLabelProps={{ shrink: true }}
            >
              {packageSubTypes.map((s) => (
                <MenuItem key={s.packageSubTypeId} value={s.packageSubTypeId}>
                  {s.packageSubTypeName}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Package Title"
              size="small"
              fullWidth
              value={formData.packageTitle}
              onChange={(e) =>
                handleChange("packageTitle", e.target.value)
              }
              sx={fieldStyle}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Package Details"
              multiline
              rows={3}
              size="small"
              fullWidth
              value={formData.packageDetails}
              onChange={(e) =>
                handleChange("packageDetails", e.target.value)
              }
              sx={fieldStyle}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Per Camera Price"
              type="number"
              size="small"
              fullWidth
              value={formData.packagePrice}
              onChange={(e) =>
                handleChange("packagePrice", e.target.value)
              }
              sx={fieldStyle}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Box>

      
        <Box sx={{ p: 3, borderTop: "1px solid #c4c4c4" }}>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#184459ff" }}
              onClick={handleSave}
              startIcon={loading && <CircularProgress size={18} />}
            >
              {loading ? "Saving..." : packageData ? "Update Package" : "Add Package"}
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#757575ff" }}
              onClick={onClose}
            >
              Close
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default AddPackageModal;
